import React, { useState } from 'react';

import { Alert, Button, Stack, TextField, Typography } from '@mui/material';

import { toPromoBrand } from '~/consts/promoBrand';

import { useGetInternalVouchersCount } from '~/hooks/apis/promo/useGetMyVouchers';

import isUUID from '~/utils/isUUID';

type Props = {
  brand: string;
  userId: string;
};

export default function MyVouchersCountTester({ brand, userId }: Props) {
  const [region, setRegion] = useState('AU');
  const [reportingTag, setReportingTag] = useState('');
  const [cartId, setCartId] = useState('');
  const normalizedCartId = cartId.trim();
  const invalidCartId = Boolean(normalizedCartId) && !isUUID(normalizedCartId);
  const promoBrand = toPromoBrand(brand);
  const { data, error, isFetching, refetch } = useGetInternalVouchersCount({
    userId: userId,
    brand: promoBrand,
    region: region.trim() || undefined,
    voucherReportingTag: reportingTag.trim() || undefined,
    cartId: normalizedCartId || undefined,
  });

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">4. GET /internal/my-vouchers/count</Typography>
      <Typography variant="body2" color="text.secondary">
        Count vouchers for the user above using promo-admin permission. Optional cart context does not change the count.
      </Typography>
      <TextField label="Brand" value={brand} InputProps={{ readOnly: true }} helperText="Uses the selected tenant." />
      {!promoBrand && <Alert severity="error">The selected tenant is not a supported promo brand.</Alert>}
      <TextField label="Region" value={region} onChange={(event) => setRegion(event.target.value)} />
      <TextField
        label="Voucher reporting tag"
        value={reportingTag}
        onChange={(event) => setReportingTag(event.target.value)}
        helperText="Exact tag match; leave empty to count all eligible vouchers."
      />
      <TextField
        label="Cart ID"
        value={cartId}
        onChange={(event) => setCartId(event.target.value)}
        error={invalidCartId}
        helperText={invalidCartId ? 'Enter a valid cart UUID.' : 'Optional checkout cart transaction key.'}
      />
      <Stack direction="row">
        <Button
          variant="contained"
          color="primary"
          disabled={!userId || !promoBrand || invalidCartId || isFetching}
          onClick={() => void refetch()}
        >
          {isFetching ? 'Loading...' : 'Run GET /internal/my-vouchers/count'}
        </Button>
      </Stack>
      {error && <Alert severity="error">{error.message}</Alert>}
      {data && <Typography>Voucher count: {data.result.count}</Typography>}
      <TextField
        label="Count response"
        value={data ? JSON.stringify(data, null, 2) : ''}
        fullWidth
        multiline
        minRows={5}
        InputProps={{ readOnly: true }}
      />
    </Stack>
  );
}
